import React, { useEffect, useState } from "react";
import DashaPeriods from "@/components/DashaPeriods";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Loader2, Compass, Download, MessageCircle, Save, Check, Sunrise } from "lucide-react";
import KundliChart from "@/components/KundliChart";
import EastIndianChart from "@/components/EastIndianChart";
import KundliAnalysisPanel from "@/components/KundliAnalysisPanel";
import CitySearchInput from "@/components/CitySearchInput";
import { formatApiError, API_BASE } from "@/lib/api";

/**
 * Reusable Kundli generator panel.
 * Props:
 *  - initial: { dob, tob, place_of_birth } prefilled
 *  - clientId: if present, save computed astrology fields back to that client
 *  - onSaved(kundli): optional callback
 */
export default function KundliPanel({ initial = {}, clientId, onSaved, recordId }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    dob: initial.dob || "",
    tob: initial.tob || "",
    place_of_birth: initial.place_of_birth || "",
    lat: "",
    lon: "",
    tz: "",
  });
  const [kundli, setKundli] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!recordId) return;
    api.get(`/kundli-history/${recordId}`).then(({ data }) => {
      setKundli(data.kundli);
      setAnalysis(data.analysis || "");
      setSaved({ id: data.id, serial_no: data.serial_no });
      const b = data.kundli.birth || {};
      setForm(f => ({ ...f, name: data.client_name || "", dob: b.dob || "", tob: b.tob || "", place_of_birth: b.place || "", lat: b.lat ?? "", lon: b.lon ?? "", tz: b.tz || "" }));
    }).catch(() => toast.error("Saved Kundli not found"));
  }, [recordId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/kundli/save", { kundli, analysis, client_id: clientId, name: form.name || undefined });
      setSaved(data);
      toast.success(`Kundli saved · ${data.serial_no}`);
      onSaved && onSaved(kundli);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const generate = async (with_ai = true) => {
    if (!form.dob) return toast.error("Date of birth required");
    if (!form.place_of_birth && (!form.lat || !form.lon)) {
      return toast.error("Provide a place of birth OR manual latitude & longitude");
    }
    setBusy(true);
    try {
      const payload = { ...form, with_ai };
      if (!payload.lat) delete payload.lat; else payload.lat = parseFloat(payload.lat);
      if (!payload.lon) delete payload.lon; else payload.lon = parseFloat(payload.lon);
      if (!payload.tz) delete payload.tz;
      if (clientId) payload.client_id = clientId;
      const { data } = await api.post("/kundli/generate", payload);
      setKundli(data.kundli);
      setAnalysis(data.analysis || "");
      setSaved(null);
      toast.success("Kundli generated");
      onSaved && onSaved(data.kundli);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kundli failed");
    } finally { setBusy(false); }
  };

  const printChart = () => window.print();

  const downloadPdf = async (styleOverride) => {
    if (!kundli) return;
    const style = styleOverride || chartStyle;
    try {
      const t = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/kundli/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ kundli, analysis, client_id: clientId, client_name: form.name || undefined, chart_style: style, record_id: saved?.id }),
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = style === "east" ? "East" : "North";
      a.download = `${(form.name || "Kundli").replace(/\s+/g, "_")}_Kundli_${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${suffix}-Indian PDF downloaded`);
    } catch { toast.error("PDF failed"); }
  };

  const [chartView, setChartView] = useState("D1");
  const [chartStyle, setChartStyle] = useState("north"); // north | east
  const activeChart = chartView === "D9" ? kundli?.navamsa : chartView === "D10" ? kundli?.dashamsa : kundli;
  const chartTitle = { D1: "Rashi (D-1)", D9: "Navamsa (D-9)", D10: "Dashamsa (D-10)" }[chartView];
  const ChartComp = chartStyle === "east" ? EastIndianChart : KundliChart;

  return (
    <div className="space-y-6" data-testid="kundli-panel">
      <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Full name</Label>
            <Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="kundli-name" placeholder="Full name for the report" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Date of birth *</Label>
            <Input type="date" value={form.dob} onChange={e=>setForm({...form,dob:e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="kundli-dob" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Time of birth</Label>
            <Input type="time" value={form.tob} onChange={e=>setForm({...form,tob:e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="kundli-tob" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Place of birth</Label>
            <div className="mt-1">
              <CitySearchInput
                value={form.place_of_birth}
                onChange={(v) => setForm(f => ({ ...f, place_of_birth: v }))}
                onSelect={(c) => setForm(f => ({ ...f, place_of_birth: c.label, lat: c.lat, lon: c.lon, tz: c.timezone || "" }))}
                placeholder="Type any city, hamlet or town…"
                testid="kundli-place"
              />
            </div>
          </div>
        </div>
        {/* Manual lat/lon override — for any place NOT found in autocomplete */}
        <details className="mt-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-3" data-testid="manual-latlon-details">
          <summary className="cursor-pointer text-xs uppercase tracking-widest text-slate-500 select-none">
            Place not found? Enter latitude / longitude manually
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Latitude (°)</Label>
              <Input type="number" step="0.0001" value={form.lat} onChange={e=>setForm({...form, lat: e.target.value})}
                     className="mt-1 h-10 rounded-xl" placeholder="e.g. 20.2961" data-testid="kundli-lat" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Longitude (°)</Label>
              <Input type="number" step="0.0001" value={form.lon} onChange={e=>setForm({...form, lon: e.target.value})}
                     className="mt-1 h-10 rounded-xl" placeholder="e.g. 85.8245" data-testid="kundli-lon" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Timezone (IANA)</Label>
              <Input value={form.tz} onChange={e=>setForm({...form, tz: e.target.value})}
                     className="mt-1 h-10 rounded-xl" placeholder="e.g. Asia/Kolkata (auto)" data-testid="kundli-tz" />
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            Tip: If you enter latitude &amp; longitude, they override the city search. Timezone is auto-detected from lat/lon when left blank.
          </p>
        </details>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button onClick={() => generate(true)} disabled={busy} className="bg-[#1E3A8A] btn-pill" data-testid="kundli-generate-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Compass className="w-4 h-4 mr-1" />} Generate Kundli + AI Analysis
          </Button>
          <Button variant="outline" onClick={() => generate(false)} disabled={busy} data-testid="kundli-chart-only-btn">Chart only</Button>
          {kundli && (
            <>
              {saved ? (
                <Badge className="h-9 px-3 bg-emerald-100 text-emerald-800 font-mono" data-testid="kundli-saved-serial">
                  <Check className="w-3.5 h-3.5 mr-1" /> Saved · {saved.serial_no}
                </Badge>
              ) : (
                <Button onClick={save} disabled={saving} className="btn-pill bg-emerald-600 hover:bg-emerald-700" data-testid="kundli-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Kundli
                </Button>
              )}
              <Button variant="outline" onClick={() => downloadPdf("north")} data-testid="kundli-pdf-north-btn">
                <Download className="w-4 h-4 mr-1" /> PDF · North
              </Button>
              <Button variant="outline" onClick={() => downloadPdf("east")} data-testid="kundli-pdf-east-btn"
                className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-500/40 dark:text-amber-400">
                <Download className="w-4 h-4 mr-1" /> PDF · East
              </Button>
            </>
          )}
          {kundli && clientId && <WhatsAppButton clientId={clientId} kundli={kundli} testid="kundli-whatsapp-btn" />}
          {kundli && <Button variant="ghost" onClick={printChart}>Print</Button>}
        </div>
        <p className="mt-2 text-xs text-slate-500">Search any of 34,000+ cities worldwide — lat/lon &amp; timezone auto-fill on selection.</p>
      </Card>

      {kundli && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-gradient-to-br from-white to-amber-50/40 dark:from-slate-900/60 dark:to-amber-500/5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display text-xl flex items-center gap-2"><Compass className="w-5 h-5 text-amber-500" /> {chartTitle}</h3>
              <div className="flex items-center gap-2">
                <div className="flex rounded-full border border-slate-200 dark:border-slate-800 p-0.5">
                  {["north","east"].map(v => (
                    <button key={v} onClick={()=>setChartStyle(v)} data-testid={`chart-style-${v}`}
                      className={`px-3 h-7 rounded-full text-[10px] uppercase tracking-widest ${chartStyle === v ? "bg-amber-500 text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{v}</button>
                  ))}
                </div>
                <div className="flex rounded-full border border-slate-200 dark:border-slate-800 p-0.5">
                  {["D1","D9","D10"].map(v => (
                    <button key={v} onClick={()=>setChartView(v)} data-testid={`chart-view-${v}`}
                      className={`px-3 h-7 rounded-full text-[10px] uppercase tracking-widest ${chartView === v ? "bg-[#1E3A8A] text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>{v}</button>
                  ))}
                </div>
              </div>
            </div>
            <ChartComp kundli={activeChart} />
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
              <Info label="Lagna" value={kundli.ascendant.rashi} sub={`${kundli.ascendant.nakshatra} · Pada ${kundli.ascendant.pada}`} />
              <Info label="Moon" value={kundli.moon_sign.rashi} sub={kundli.moon_sign.nakshatra} />
              <Info label="Sun" value={kundli.sun_sign.rashi} sub={`Lord ${kundli.sun_sign.lord}`} />
            </div>
            {kundli.birth?.sunrise && (
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs" data-testid="kundli-sunrise-info">
                <Info label="Sunrise" value={kundli.birth.sunrise.slice(0, 5)} sub={`Sunset ${kundli.birth.sunset?.slice(0, 5)}`} />
                <Info label="Ishta Kaal" value={kundli.birth.ishta_kaal} sub={`${kundli.birth.day_night} birth`} />
                <Info label="Vedic Day" value={kundli.birth.vedic_weekday} sub={kundli.birth.born_before_sunrise ? `Before sunrise (cal. ${kundli.birth.calendar_weekday})` : "After sunrise"} />
              </div>
            )}
            <div className="mt-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-sm">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-400">Current Mahadasha</div>
              <div className="font-display text-xl mt-1">{kundli.dasha.current.planet}</div>
              <div className="text-xs text-slate-600 dark:text-slate-300">
                Till {kundli.dasha.current.end.slice(0,10)} · Remaining {kundli.dasha.current.years} yr window
              </div>
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
            <h3 className="font-display text-xl mb-3">Planetary Positions</h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Planet</th>
                    <th className="text-left px-3 py-2">Rashi</th>
                    <th className="text-left px-3 py-2">Nakshatra</th>
                    <th className="text-center px-2 py-2">House</th>
                  </tr>
                </thead>
                <tbody>
                  {kundli.planets.map(p => (
                    <tr key={p.name} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-3 py-1.5 font-medium">{p.name}{p.retrograde && <span className="ml-1 text-amber-600">℞</span>}</td>
                      <td className="px-3 py-1.5">{p.rashi} <span className="text-xs text-slate-500">({p.degrees_in_sign?.toFixed(1)}°)</span></td>
                      <td className="px-3 py-1.5 text-slate-600 dark:text-slate-300">{p.nakshatra} · Pd {p.pada}</td>
                      <td className="px-2 py-1.5 text-center font-mono text-amber-600">{p.house}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 lg:col-span-2">
            <h3 className="font-display text-xl mb-3">Vimshottari Mahadasha Sequence</h3>
            <div className="flex flex-wrap gap-2">
              {kundli.dasha.sequence.map((d) => (
                <div key={`dasha-${d.planet}-${d.start}`} className={`px-3 py-2 rounded-xl border text-xs ${d.planet === kundli.dasha.current.planet ? "bg-amber-50 dark:bg-amber-500/10 border-amber-300" : "border-slate-200 dark:border-slate-800"}`}>
                  <div className="font-medium">{d.planet}</div>
                  <div className="text-[10px] font-mono text-slate-500">{d.start.slice(0,10)} → {d.end.slice(0,10)}</div>
                  <div className="text-[10px] text-slate-400">{d.years} yr</div>
                </div>
              ))}
            </div>
          </Card>

          <DashaPeriods periods={kundli.dasha_periods} />

          {analysis && (
            <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 lg:col-span-2 bg-gradient-to-br from-amber-50/40 to-transparent dark:from-amber-500/5" data-testid="kundli-analysis">
              <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Generic Vedic Analysis</h3>
              <div className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{analysis}</div>
              <div className="mt-4 text-[11px] italic text-slate-500">
                This is an automated generic reading based on planetary placements. For personalised guidance, book a consultation with Astrologer Trinandan Mishra.
              </div>
            </Card>
          )}

          <div className="lg:col-span-2">
            <KundliAnalysisPanel kundli={kundli} />
          </div>
        </div>
      )}
    </div>
  );
}

const Info = ({ label, value, sub }) => (
  <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-lg text-slate-900 dark:text-white">{value}</div>
    {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
  </div>
);

function WhatsAppButton({ clientId, kundli, testid }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      // Fetch client info for phone number
      const { data: client } = await api.get(`/clients/${clientId}`);
      const phone = (client.whatsapp || client.mobile || "").replace(/\D/g, "");
      if (!phone) {
        toast.error("Client has no WhatsApp or mobile number on file");
        setBusy(false); return;
      }
      // 1. Download PDF first
      const t = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/kundli/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ kundli, client_id: clientId }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${(client.full_name || "Kundli").replace(/\s+/g, "_")}_Kundli.pdf`; a.click();
        URL.revokeObjectURL(url);
      }
      // 2. Build message
      const asc = kundli.ascendant?.rashi || "—";
      const moon = kundli.moon_sign?.rashi || "—";
      const dasha = kundli.dasha?.current?.planet || "—";
      const message = `Namaste ${client.full_name} ji 🙏

Your Vedic Kundli report is ready.

Lagna (Ascendant): ${asc}
Moon Sign: ${moon}
Current Mahadasha: ${dasha}

The complete PDF has just been downloaded on my device — attaching it here below. Please review at your convenience. For any questions or a detailed reading, feel free to reply.

Sincerely,
Team Astrologer Trinandan Mishra`;
      // 3. Open wa.me
      const waNumber = phone.length === 10 ? `91${phone}` : phone;
      const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("PDF downloaded — attach it in the WhatsApp window that just opened");
    } catch (e) {
      toast.error("Send failed");
    }
    setBusy(false);
  };
  return (
    <Button variant="outline" onClick={send} disabled={busy} data-testid={testid} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/40 dark:text-emerald-400">
      {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-1" />} Send on WhatsApp
    </Button>
  );
}

